import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiLink } from "react-icons/fi";
import { Sparkles } from "lucide-react";
import WizardLayout from "../../components/common/formkit/WizardLayout";
import MediaUploader from "../../components/common/formkit/MediaUploader";
import ReviewCard from "../../components/common/formkit/ReviewCard";
import { FieldShell, Section, SelectField, TextField } from "../../components/common/formkit/fields";
import { useExitWizard, useUnsavedChangesGuard, allowLeaveOnce } from "../../components/common/formkit/formHelpers";
import { showToast } from "../../components/common/notification/Notification";
import PromotionCard from "./components/PromotionCard";
import { createPromotionService } from "./services/promotionService";
import { usePromotionApiClient } from "./services/usePromotionApiClient";
import { resolveAssetUrl, stripHtml } from "./utils/formatters";

const PROMOTIONS_HOME = "/partner/my-promotions";
const MEDIA_MAX_MB = 20; // same per-file limit as the former popup
const VIDEO_PATTERN = /\.(mp4|mov|webm|m4v|avi|mkv)$/i;

const STEP_FIELDS = {
  details: ["project_name", "type_id", "starting_cost", "state", "city", "area_name", "pin_code", "promotion_url"],
};

// ---- Starting cost: accepts 5000000, 50,00,000, ₹50 Lakh, 5.2 cr ... ----
const COST_PATTERN = /^(\d+(?:\.\d+)?)(cr|crore|crores|l|lac|lacs|lakh|lakhs|k|thousand)?$/;
const COST_UNITS = { cr: 1e7, crore: 1e7, crores: 1e7, l: 1e5, lac: 1e5, lacs: 1e5, lakh: 1e5, lakhs: 1e5, k: 1e3, thousand: 1e3 };

const parseCost = (raw) => {
  const match = COST_PATTERN.exec(String(raw ?? "").toLowerCase().replace(/[₹,\s]/g, ""));
  if (!match) return NaN;
  return Number(match[1]) * (match[2] ? COST_UNITS[match[2]] : 1);
};

const trimDecimals = (n) => String(Number(n.toFixed(2)));

const costInWords = (n) => {
  if (!(n > 0)) return "";
  if (n >= 1e7) return `₹${trimDecimals(n / 1e7)} Crore`;
  if (n >= 1e5) return `₹${trimDecimals(n / 1e5)} Lakh`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const validationSchema = Yup.object({
  project_name: Yup.string().trim().required("Project name is required"),
  type_id: Yup.string().required("Property type is required"),
  starting_cost: Yup.string()
    .required("Starting cost is required")
    .test("cost", "Enter a valid amount, e.g. 5000000 or ₹50 Lakh", (v) => !v || parseCost(v) > 0),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  pin_code: Yup.string().test("pin", "PIN code must be 6 digits", (v) => !v || /^\d{6}$/.test(v)),
  promotion_url: Yup.string().test(
    "url",
    "Enter a valid link starting with http:// or https://",
    (v) => !v || /^https?:\/\/[^\s/$.?#][^\s]*\.[^\s]{2,}$/i.test(v.trim())
  ),
});

const toOptions = (list) => (list || []).map((item) => ({ value: item.id, label: item.name }));

const CostField = () => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext();
  const error = touched.starting_cost && errors.starting_cost ? errors.starting_cost : null;
  const words = costInWords(parseCost(values.starting_cost));
  return (
    <FieldShell
      id="starting_cost"
      label="Starting Cost"
      required
      error={error}
      helper={words || "e.g. 5000000 or ₹50 Lakh"}
    >
      <div className="fk-affix">
        <span className="fk-affix__pre">₹</span>
        <input
          id="starting_cost"
          name="starting_cost"
          type="text"
          inputMode="decimal"
          className="fk-input"
          placeholder="e.g. 5000000 or 50 Lakh"
          value={values.starting_cost}
          onChange={(e) => setFieldValue("starting_cost", e.target.value)}
          onBlur={() => {
            const n = parseCost(values.starting_cost);
            if (n > 0) setFieldValue("starting_cost", n.toLocaleString("en-IN", { maximumFractionDigits: 2 }));
            setFieldTouched("starting_cost", true);
          }}
          aria-invalid={error ? "true" : undefined}
          aria-required="true"
          aria-describedby={error ? "starting_cost-error" : "starting_cost-help"}
        />
      </div>
    </FieldShell>
  );
};

const PinField = () => {
  const { setFieldValue } = useFormikContext();
  return (
    <TextField
      name="pin_code"
      label="PIN Code"
      placeholder="e.g. 411001"
      inputMode="numeric"
      maxLength={6}
      autoComplete="postal-code"
      onChange={(e) => setFieldValue("pin_code", e.target.value.replace(/\D/g, "").slice(0, 6))}
    />
  );
};

const DescriptionField = ({ promotionService }) => {
  const { values, setFieldValue } = useFormikContext();
  const [showAi, setShowAi] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const length = stripHtml(values.project_description).length;

  const generate = async () => {
    const input =
      aiPrompt.trim() ||
      `${values.project_name || "Luxury residential property"} in ${
        values.area_name || values.city_name || "prime location"
      } starting at ${values.starting_cost || "attractive pricing"} with premium modern amenities.`;
    try {
      setGenerating(true);
      const data = await promotionService.generateAiDescription(input);
      const text = data?.description || data?.generated_text || data?.searchQueryResponse || data?.data || data?.text || "";
      if (typeof text === "string" && text) {
        setFieldValue("project_description", text);
        showToast("AI generated description successfully!", { type: "success" });
      } else {
        showToast("No description returned by AI service", { type: "error" });
      }
    } catch {
      showToast("Failed to generate AI description", { type: "error" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <button type="button" className="fk-btn fk-btn--ghost" onClick={() => setShowAi((v) => !v)} aria-expanded={showAi}>
          <Sparkles size={14} /> {showAi ? "Close AI prompt" : "Write with AI"}
        </button>
        {showAi && (
          <>
            <input
              type="text"
              className="fk-input"
              style={{ flex: "1 1 240px", width: "auto" }}
              aria-label="AI prompt"
              placeholder="Enter keywords (e.g. Exclusive 2 & 3 BHK luxury flats with infinity pool)"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
            />
            <button type="button" className="fk-btn fk-btn--primary" onClick={generate} disabled={generating}>
              {generating && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
              Generate
            </button>
          </>
        )}
      </div>
      <FieldShell id="project_description" label="Project Description">
        <div className="fk-quill">
          <ReactQuill
            theme="snow"
            value={values.project_description}
            onChange={(val) => setFieldValue("project_description", val)}
            placeholder="Provide highlights, amenities, floor plans, and promoter details..."
          />
        </div>
        <div className="fk-counter">
          <span>{length} characters</span>
        </div>
      </FieldShell>
    </>
  );
};

const PromotionWizardBody = ({ isEdit, promotionService, propertyTypes, states, media, onSubmit, submitting, saved }) => {
  const formik = useFormikContext();
  const { values, touched, setTouched, setFieldValue, validateForm } = formik;
  const exit = useExitWizard(PROMOTIONS_HOME);
  const authUser = useSelector((state) => state.auth?.user);
  const partnerName = [authUser?.user?.first_name, authUser?.user?.last_name].filter(Boolean).join(" ");

  // Media & Docs is create-only: the edit endpoint does not take new files
  const steps = [
    { key: "details", title: "Details", description: "Project info and location" },
    ...(isEdit ? [] : [{ key: "media", title: "Media & Docs", description: "Photos, videos, brochures" }]),
    { key: "review", title: "Review", description: isEdit ? "Confirm and save" : "Confirm and publish" },
  ];
  const [currentKey, setCurrentKey] = useState("details");
  const [reachedKeys, setReachedKeys] = useState([]);
  const index = steps.findIndex((s) => s.key === currentKey);
  const isLast = currentKey === "review";

  const mediaCount = media.images.length + media.videos.length + media.attachments.length;
  useUnsavedChangesGuard((formik.dirty || mediaCount > 0) && !submitting && !saved);

  const [cities, setCities] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!values.state) {
      setCities([]);
      return undefined;
    }
    promotionService.fetchCities(values.state).then((list) => {
      if (!cancelled) setCities(list || []);
    });
    return () => {
      cancelled = true;
    };
  }, [values.state, promotionService]);

  const focusFirstInvalid = () =>
    setTimeout(() => {
      const el = document.querySelector('.fk-card [aria-invalid="true"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        (el.matches("button, input") ? el : el.querySelector("button, input"))?.focus({ preventScroll: true });
      }
    }, 0);

  const handleNext = async () => {
    if (isLast) {
      // Last guard before publishing: send the user back to Details if anything is invalid
      const allErrors = await validateForm();
      if (STEP_FIELDS.details.some((f) => allErrors[f])) {
        setTouched(Object.fromEntries(STEP_FIELDS.details.map((f) => [f, true])), false);
        setCurrentKey("details");
        focusFirstInvalid();
        return;
      }
      onSubmit(values);
      return;
    }
    const fields = STEP_FIELDS[currentKey] || [];
    if (fields.length) {
      const allErrors = await validateForm();
      setTouched({ ...touched, ...Object.fromEntries(fields.map((f) => [f, true])) }, false);
      if (fields.some((f) => allErrors[f])) {
        focusFirstInvalid();
        return;
      }
    }
    setReachedKeys((prev) => (prev.includes(currentKey) ? prev : [...prev, currentKey]));
    setCurrentKey(steps[index + 1].key);
  };

  // Jumping ahead is blocked until the details are valid
  const handleStepClick = (key) => {
    if (steps.findIndex((s) => s.key === key) < index) setCurrentKey(key);
    else if (reachedKeys.includes(key)) setCurrentKey(key);
  };

  // ---- media ----
  const mediaItems = () => {
    const build = (list, kind, urlOf, nameOf) =>
      list.map((m, i) => ({
        id: `${kind}-${i}-${urlOf(m)}`,
        kind,
        url: urlOf(m),
        name: nameOf(m),
        index: i,
        isLast: i === list.length - 1,
      }));
    return {
      visual: [
        ...build(media.images, "image", (m) => resolveAssetUrl(m.image_url || m.imagePath), (m) => m.name),
        ...build(media.videos, "video", (m) => resolveAssetUrl(m.url_path), (m) => m.name),
      ],
      docs: build(media.attachments, "pdf", (m) => m.attachment_url, (m) => m.name),
    };
  };
  const setterOf = { image: media.setImages, video: media.setVideos, pdf: media.setAttachments };

  const handleUploaded = ({ media: uploaded, attachments }) => {
    const isVideo = (u) => VIDEO_PATTERN.test(u.imagePath || u.image_url || u.name || "");
    media.setImages((prev) => [
      ...prev,
      ...uploaded.filter((u) => !isVideo(u)).map((u) => ({
        imagePath: u.imagePath || u.image_url,
        name: u.name || u.imagePath,
        description: "Promotion Image",
        image_url: u.image_url || u.imagePath,
        type: "image",
      })),
    ]);
    media.setVideos((prev) => [
      ...prev,
      ...uploaded.filter(isVideo).map((u) => ({
        name: u.name || u.imagePath,
        url_path: u.image_url || u.imagePath,
        description: "Video",
      })),
    ]);
    media.setAttachments((prev) => [
      ...prev,
      ...attachments.map((doc, i) => ({
        attachmentPath: doc.attachmentPath || doc.attachment_url,
        name: doc.name || doc.attachmentPath || `attachment_${prev.length + i + 1}`,
        description: "Promotion Document",
        attachment_url: doc.attachment_url || doc.attachmentPath,
      })),
    ]);
  };
  const handleRemove = (item) => setterOf[item.kind]((prev) => prev.filter((_, i) => i !== item.index));
  const handleMove = (item, direction) =>
    setterOf[item.kind]((prev) => {
      const list = [...prev];
      const target = item.index + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[item.index], list[target]] = [list[target], list[item.index]];
      return list;
    });

  const typeName = propertyTypes.find((t) => String(t.id) === String(values.type_id))?.name;

  const renderStep = () => {
    if (currentKey === "details") {
      return (
        <>
          <Section title="Basic Info" hint="Name your campaign and set its property type and starting price.">
            <div className="fk-grid">
              <TextField
                name="project_name"
                label="Project / Promotion Name"
                required
                className="fk-col-full"
                placeholder="e.g. Luxury Residency Skyline Towers"
              />
              <SelectField
                name="type_id"
                label="Property Type"
                required
                placeholder="Select property type"
                options={toOptions(propertyTypes)}
              />
              <CostField />
            </div>
          </Section>
          <Section title="Location" hint="Where the project is located.">
            <div className="fk-grid">
              <SelectField
                name="state"
                label="State"
                required
                options={toOptions(states)}
                onSelect={(value, option) => {
                  setFieldValue("state_name", option.label);
                  setFieldValue("city", "");
                  setFieldValue("city_name", "");
                }}
              />
              <SelectField
                name="city"
                label="City"
                required
                disabled={!values.state}
                helper={!values.state ? "Select a state first" : undefined}
                options={toOptions(cities)}
                onSelect={(value, option) => setFieldValue("city_name", option.label)}
              />
              <TextField name="area_name" label="Area / Locality" placeholder="e.g. Koregaon Park / Whitefield" />
              <PinField />
            </div>
          </Section>
          <Section title="Links" hint="Optional website or showcase page for this project.">
            <TextField
              name="promotion_url"
              label="Promotion / Project URL"
              type="url"
              prefix={<FiLink size={16} aria-hidden="true" />}
              placeholder="https://example.com/project-showcase"
            />
          </Section>
          <Section title="Description" hint="Highlights, amenities, floor plans and promoter details.">
            <DescriptionField promotionService={promotionService} />
          </Section>
        </>
      );
    }
    if (currentKey === "media") {
      const { visual, docs } = mediaItems();
      return (
        <>
          <Section title="Property Media" hint="High-res JPG or PNG images and MP4 promotion videos.">
            <MediaUploader
              items={visual}
              kinds={["image", "video"]}
              label="Upload images or videos"
              maxSizeMb={MEDIA_MAX_MB}
              onUploaded={handleUploaded}
              onRemove={handleRemove}
              onMove={handleMove}
            />
          </Section>
          <Section title="Documents & Brochures" hint="Official brochures, floor plans and specification sheets (PDF, DOC, DOCX).">
            <MediaUploader
              items={docs}
              kinds={["doc"]}
              label="Upload documents"
              maxSizeMb={MEDIA_MAX_MB}
              onUploaded={handleUploaded}
              onRemove={handleRemove}
              onMove={handleMove}
            />
          </Section>
        </>
      );
    }

    const { visual, docs } = mediaItems();
    const preview = {
      project_name: values.project_name || "Untitled Promotion",
      project_description: values.project_description,
      starting_cost: values.starting_cost ? String(parseCost(values.starting_cost) || "") : "",
      city_name: values.city_name,
      state_name: values.state_name,
      area_name: values.area_name,
      property_type: { name: typeName },
      partner: { first_name: partnerName || "You" },
      promotion_url: values.promotion_url,
      images: media.images,
    };
    return (
      <div className="fk-review-list">
        <div className="fk-section__head" style={{ margin: 0 }}>
          <h2 className="fk-section__title">{isEdit ? "Review your changes" : "Review your promotion"}</h2>
          <p className="fk-section__hint">Check everything below, then {isEdit ? "save" : "publish"}.</p>
        </div>
        {isEdit && (
          <div className="alert alert-info mb-0" role="note">
            Name, description, starting cost, property type and location can be changed here. Media, documents,
            PIN code and URL can only be set when a promotion is created.
          </div>
        )}
        <ReviewCard
          title="Basic Info"
          onEdit={() => setCurrentKey("details")}
          rows={[
            { label: "Project name", value: values.project_name },
            { label: "Property type", value: typeName },
            { label: "Starting cost", value: costInWords(parseCost(values.starting_cost)) },
          ]}
        />
        <ReviewCard
          title="Location"
          onEdit={() => setCurrentKey("details")}
          rows={[
            { label: "State", value: values.state_name },
            { label: "City", value: values.city_name },
            { label: "Area / Locality", value: values.area_name },
            { label: "PIN code", value: values.pin_code },
          ]}
        />
        <ReviewCard
          title="Links & Description"
          onEdit={() => setCurrentKey("details")}
          rows={[
            { label: "Promotion URL", value: values.promotion_url, full: true },
            { label: "Description", value: stripHtml(values.project_description), full: true },
          ]}
        />
        {!isEdit && (
          <ReviewCard
            title="Media & Docs"
            onEdit={() => setCurrentKey("media")}
            rows={[
              { label: "Images & videos", value: visual.length },
              { label: "Documents", value: docs.length },
            ]}
          />
        )}
        <div>
          <h3 className="fk-review__title" style={{ marginBottom: 12 }}>
            How it will look on My Promotions
          </h3>
          <div aria-hidden="true" style={{ maxWidth: 380, pointerEvents: "none" }}>
            <PromotionCard promotion={preview} onViewDetails={() => {}} onOpenComments={() => {}} onDelete={() => {}} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <WizardLayout
      title={isEdit ? "Edit Promotion" : "Create Promotion"}
      subtitle={
        isEdit
          ? "Update your promotion details below."
          : "Showcase your project with a campaign that reaches buyers across the marketplace."
      }
      steps={steps}
      currentKey={currentKey}
      reachedKeys={reachedKeys}
      onStepClick={handleStepClick}
      onBack={() => index > 0 && setCurrentKey(steps[index - 1].key)}
      onCancel={exit}
      onNext={handleNext}
      isLast={isLast}
      nextLabel={isLast ? (isEdit ? "Save Changes" : "Publish Promotion") : "Next"}
      submitting={submitting}
    >
      <div aria-live="polite" className="visually-hidden">
        {submitting ? "Submitting promotion" : ""}
      </div>
      {renderStep()}
    </WizardLayout>
  );
};

const initialFormValues = {
  project_name: "",
  type_id: "",
  starting_cost: "",
  state: "",
  state_name: "",
  city: "",
  city_name: "",
  area_name: "",
  pin_code: "",
  promotion_url: "",
  project_description: "",
};

const StatusPage = ({ tone, message, actionLabel, onAction }) => (
  <div className="fk-page" style={{ maxWidth: 1200 }}>
    <div className={`alert alert-${tone} d-flex align-items-center justify-content-between`} role="alert">
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onAction}>
        {actionLabel}
      </button>
    </div>
  </div>
);

/** /partner/my-promotions/new and /partner/my-promotions/:Id/edit (replaces the Create Promotion popup) */
const PromotionFormPage = () => {
  const { Id } = useParams();
  const isEdit = Boolean(Id);
  const navigate = useNavigate();

  const authUser = useSelector((state) => state.auth?.user);
  const currentUserId = authUser?.user?.id || authUser?.id || authUser?.userDetails?.id;

  const apiClient = usePromotionApiClient();
  const promotionService = useMemo(() => createPromotionService(apiClient), [apiClient]);

  const [propertyTypes, setPropertyTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [initialValues, setInitialValues] = useState(initialFormValues);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null); // 'notFound' | 'forbidden' | { error }
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const media = { images, videos, attachments, setImages, setVideos, setAttachments };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [stateList, typeList] = await Promise.all([
          promotionService.fetchStates(1),
          promotionService.fetchPropertyTypes(),
        ]);
        if (cancelled) return;
        setStates(stateList || []);
        setPropertyTypes(typeList || []);

        if (isEdit) {
          const data = await promotionService.getDetails(Id).catch(() => null);
          const promo = data?.promotion;
          if (cancelled) return;
          if (!promo) {
            setProblem("notFound");
            return;
          }
          const ownerId = promo.partner?.id;
          if (ownerId && currentUserId && Number(ownerId) !== Number(currentUserId)) {
            setProblem("forbidden");
            return;
          }
          const stateMatch = (stateList || []).find((s) => s.name === promo.state_name);
          const cityList = stateMatch ? await promotionService.fetchCities(stateMatch.id) : [];
          const cityMatch = cityList.find((c) => c.name === promo.city_name);
          if (cancelled) return;
          setInitialValues({
            ...initialFormValues,
            project_name: promo.project_name || "",
            type_id: String(promo.type_id || promo.property_type?.id || ""),
            starting_cost: promo.starting_cost ? String(promo.starting_cost) : "",
            state: stateMatch?.id ?? "",
            state_name: promo.state_name || "",
            city: cityMatch?.id ?? "",
            city_name: promo.city_name || "",
            area_name: promo.area_name || "",
            promotion_url: promo.promotion_url || "",
            project_description: promo.project_description || "",
          });
        }
      } catch (err) {
        if (!cancelled) setProblem({ error: err?.response?.data?.text || err?.message || "Unable to load the form." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Id, promotionService]);

  const handleSubmit = async (values) => {
    // Same payload as the former popup
    const base = {
      project_name: values.project_name.trim(),
      project_description: values.project_description,
      starting_cost: String(parseCost(values.starting_cost)),
      type_id: Number(values.type_id) || 1,
      city_name: values.city_name,
      state_name: values.state_name,
      country_name: "India",
      promotion_url: values.promotion_url.trim(),
      area_name: values.area_name.trim(),
      pin_code: values.pin_code.trim(),
    };
    const payload = isEdit
      ? { id: Number(Id), ...base }
      : {
          ...base,
          images: images.map((img) => ({
            imagePath: img.imagePath || img.image_url,
            name: img.name || "image.jpg",
            description: img.description || "Promotion Image",
            image_url: img.image_url || img.imagePath,
            type: "image",
          })),
          attachements: attachments.map((att) => ({
            attachmentPath: att.attachmentPath || att.attachment_url,
            name: att.name || "brochure.pdf",
            description: att.description || "Promotion Brochure",
            attachment_url: att.attachment_url || att.attachmentPath,
          })),
          promotion_videos: videos.map((vid) => ({
            name: vid.name || "video.mp4",
            url_path: vid.url_path,
            description: vid.description || "Promotion Video",
          })),
        };

    setSubmitting(true);
    try {
      const res = isEdit ? await promotionService.edit(payload) : await promotionService.create(payload);
      if (res?.statusType === "SUCCESS" || res?.id || res?.status === 200) {
        showToast(isEdit ? "Promotion updated successfully!" : "Promotion created successfully!", { type: "success" });
        setSaved(true); // lifts the unsaved-changes warning before leaving
        allowLeaveOnce();
        setTimeout(() => navigate(PROMOTIONS_HOME), 0);
      } else {
        // Form data and uploaded files stay as entered
        showToast(res?.text || res?.message || `Failed to ${isEdit ? "update" : "create"} promotion`, { type: "error" });
        setSubmitting(false);
      }
    } catch (err) {
      showToast(err?.response?.data?.text || err?.message || "Failed to save promotion. Please try again.", {
        type: "error",
      });
      setSubmitting(false);
    }
  };

  if (problem === "notFound") {
    return (
      <StatusPage
        tone="warning"
        message="This promotion could not be found. It may have been removed."
        actionLabel="Back to My Promotions"
        onAction={() => navigate(PROMOTIONS_HOME)}
      />
    );
  }
  if (problem === "forbidden") {
    return (
      <StatusPage
        tone="danger"
        message="You can only edit your own promotions."
        actionLabel="Back to My Promotions"
        onAction={() => navigate(PROMOTIONS_HOME)}
      />
    );
  }
  if (problem?.error) {
    return (
      <StatusPage tone="danger" message={problem.error} actionLabel="Retry" onAction={() => window.location.reload()} />
    );
  }

  if (loading) {
    return (
      <WizardLayout
        title={isEdit ? "Edit Promotion" : "Create Promotion"}
        steps={[{ key: "details", title: "Details" }]}
        currentKey="details"
        loading
      />
    );
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={() => {}}>
      <PromotionWizardBody
        isEdit={isEdit}
        promotionService={promotionService}
        propertyTypes={propertyTypes}
        states={states}
        media={media}
        onSubmit={handleSubmit}
        submitting={submitting}
        saved={saved}
      />
    </Formik>
  );
};

export default PromotionFormPage;
